import { sentenceCaseTaxonomyLabel } from "./sentenceCaseTaxonomyLabel";
import enCatalog from "./taxonomyCatalog.en.json";
import enTranslations from "./taxonomyTranslations.en.json";
import esTranslations from "./taxonomyTranslations.es.json";
import ptTranslations from "./taxonomyTranslations.pt.json";
import type { ViewerLocale } from "./viewerPreferences";

export const TAXONOMY_KINDS = ["genre", "style"] as const;

export type TaxonomyKind = (typeof TAXONOMY_KINDS)[number];

export interface TaxonomyCatalog {
  genres: Readonly<Record<string, string>>;
  styles: Readonly<Record<string, string>>;
}

/**
 * `taxonomyCatalog.en.json` is generated from the catalog vocabulary seeds and keeps their display
 * names; `taxonomyTranslations.<locale>.json` overrides labels per locale, en included (e.g. the
 * hyphen-free "Hard Rock"). English labels are Title Case. Portuguese and Spanish labels are
 * sentence case: their translation files are written that way, and the English names they fall
 * back to are converted ("Bossa Nova" → "Bossa nova", "Chicago Blues" → "Chicago blues").
 */
function localizedCatalog(
  translations: Partial<TaxonomyCatalog>,
  fallbackLabel: (label: string) => string = (label) => label,
): TaxonomyCatalog {
  const fallback = (labels: Readonly<Record<string, string>>) =>
    Object.fromEntries(Object.entries(labels).map(([key, label]) => [key, fallbackLabel(label)]));
  return {
    genres: { ...fallback(enCatalog.genres), ...translations.genres },
    styles: { ...fallback(enCatalog.styles), ...translations.styles },
  };
}

export const taxonomyCatalogByLocale: Record<ViewerLocale, TaxonomyCatalog> = {
  en: localizedCatalog(enTranslations),
  es: localizedCatalog(esTranslations, sentenceCaseTaxonomyLabel),
  pt: localizedCatalog(ptTranslations, sentenceCaseTaxonomyLabel),
};
